import { Modal, Badge } from 'react-bootstrap';

import {
  SPEND_OR_REVENUE_FORMATTER,
  CPM_FORMATTER,
  IMP_FORMATTER
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';

import { BPMTable, Header } from '../../Components/StickyTable';
import { Button } from '../../Components/Button';
import { Appearance, Design } from '../../Components/designConstants';

import { conditionallyPluralize } from '../../utils/text-utils';

import { ImportReview, ImportReviewRow } from './streaming-buying-types';

import './ImportReviewModal.scss';

const ROW_HEIGHT = 35;
const HEADER_HEIGHT = 36;
const MAX_TABLE_HEIGHT = 400;

interface DiffCellProps {
  oldVal: number;
  newVal: number;
  format: (value: number) => string;
}

const DiffCell = ({ oldVal, newVal, format }: DiffCellProps) => {
  if (oldVal === newVal) {
    return <>{format(oldVal)}</>;
  }
  return (
    <>
      <span className='old-value'>{format(oldVal)}</span>
      {' → '}
      <span className='new-value'>{format(newVal)}</span>
    </>
  );
};

const HEADERS: Header[] = [
  { label: 'Order', name: 'orderId', width: 70, nonInteractive: true },
  { label: 'Network', name: 'network', width: 100, nonInteractive: true },
  { label: 'Flight ID', name: 'flightId', width: 80, nonInteractive: true },
  { label: 'Platform', name: 'platform', flex: true, nonInteractive: true },
  { label: 'Start', name: 'startDate', width: 90, nonInteractive: true },
  { label: 'End', name: 'endDate', width: 90, nonInteractive: true },
  {
    label: 'Spend',
    name: 'oldSpend',
    width: 155,
    nonInteractive: true,
    renderer: (row: ImportReviewRow) => (
      <DiffCell oldVal={row.oldSpend} newVal={row.newSpend} format={SPEND_OR_REVENUE_FORMATTER} />
    )
  },
  {
    label: 'CPM',
    name: 'oldCpm',
    width: 110,
    nonInteractive: true,
    renderer: (row: ImportReviewRow) => (
      <DiffCell oldVal={row.oldCpm} newVal={row.newCpm} format={CPM_FORMATTER} />
    )
  },
  {
    label: 'Impressions',
    name: 'oldImpressions',
    width: 155,
    nonInteractive: true,
    renderer: (row: ImportReviewRow) => (
      <DiffCell oldVal={row.oldImpressions} newVal={row.newImpressions} format={IMP_FORMATTER} />
    )
  },
  {
    label: 'Action',
    name: 'isCancellation',
    width: 80,
    nonInteractive: true,
    renderer: (row: ImportReviewRow) =>
      row.isCancellation ? (
        <Badge bg='danger'>Cancel</Badge>
      ) : (
        <Badge bg='warning' text='dark'>
          Update
        </Badge>
      )
  }
];

interface ImportReviewModalProps {
  review: ImportReview | null;
  onAccept: () => void;
  onDiscard: () => void;
}

export const ImportReviewModal = ({ review, onAccept, onDiscard }: ImportReviewModalProps) => {
  if (!review) {
    return null;
  }

  const { rows, newFlightsImported } = review;
  const cancellationCount = rows.filter(r => r.isCancellation).length;
  const updateCount = rows.length - cancellationCount;
  const tableHeight = Math.min(HEADER_HEIGHT + rows.length * ROW_HEIGHT, MAX_TABLE_HEIGHT);

  return (
    <Modal show size='xl' onHide={onDiscard} scrollable className='import-review-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Import Review</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='summary'>
          {updateCount > 0 && (
            <>
              <strong>{updateCount}</strong> budget {conditionallyPluralize(updateCount, 'update')}
              {cancellationCount > 0 ? ' and ' : ''}
            </>
          )}
          {cancellationCount > 0 && (
            <>
              <strong>{cancellationCount}</strong>{' '}
              {conditionallyPluralize(cancellationCount, 'cancellation')}
            </>
          )}
          {rows.length > 0
            ? ' pending review. After accepting, navigate to each order to save.'
            : ' — no changes detected.'}
        </p>
        {newFlightsImported > 0 && (
          <p className='new-flights-note'>
            Also imported <strong>{newFlightsImported}</strong> new{' '}
            {conditionallyPluralize(newFlightsImported, 'flight')}.
          </p>
        )}
        {rows.length > 0 && (
          <div className='import-review-table' style={{ height: tableHeight }}>
            <BPMTable
              data={rows}
              headers={HEADERS}
              rowHeight={ROW_HEIGHT}
              headerHeight={HEADER_HEIGHT}
              filterBar={false}
              alternateColors
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onDiscard}>
          Discard
        </Button>
        <Button onClick={onAccept} disabled={rows.length === 0}>
          Accept Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
